import { useEffect, useState } from "react";
import { countState } from "../../actions/studensts";
import UNAUTHORIZED from "../UNAUTHORIZED";
import { FiUsers, FiEdit, FiFileText, FiCheckCircle, FiTruck, FiAward, FiBarChart2 } from "react-icons/fi";

const StatItem = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-slate-400 text-xs">{label}</span>
    <span className="text-white font-bold text-sm">{value ?? "—"}</span>
  </div>
);

const Card = ({ icon: Icon, title, color, children }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-5 shadow hover:bg-white/[0.07] transition-colors">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center shrink-0`}>
        <Icon className="text-white text-base" />
      </div>
      <h2 className="text-white font-semibold text-sm">{title}</h2>
    </div>
    <div className="divide-y divide-white/5">{children}</div>
  </div>
);

export default function Overview() {
  const [size, setSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem("profile")).value[0].role;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await countState();
        setSize(res.data.size);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (!["admin", "auditor"].includes(user)) return <UNAUTHORIZED />;

  return (
    <div className="min-h-screen px-4 sm:px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <FiBarChart2 className="text-indigo-400" /> Overview
        </h1>
        <p className="text-slate-400 text-sm mt-1">Real-time student pipeline statistics</p>
      </div>

      {/* Total students highlight */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-800 p-6 shadow-lg mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-200 text-xs font-semibold uppercase tracking-widest">Total Students</p>
            <p className="text-white text-4xl font-bold mt-1">
              {loading ? (
                <span className="inline-block w-20 h-9 bg-indigo-500 animate-pulse rounded" />
              ) : (
                size?.total ?? 0
              )}
            </p>
          </div>
          <div className="bg-indigo-500/30 rounded-full p-4">
            <FiUsers className="text-white text-3xl" />
          </div>
        </div>
        <div className="absolute -bottom-4 -right-4 w-28 h-28 bg-indigo-500/20 rounded-full" />
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-indigo-500 mb-4" />
          <p className="text-slate-400 text-base">Loading statistics…</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Auditing */}
          <Card icon={FiEdit} title="Auditing" color="bg-amber-600">
            <StatItem label="New" value={size?.auditing} />
            <StatItem label="Request Edit" value={size?.request_edit} />
            <StatItem label="Total" value={size?.total_auditing} />
          </Card>

          {/* Invoice */}
          <Card icon={FiFileText} title="Invoice" color="bg-emerald-600">
            <StatItem label="New" value={size?.invoice} />
            <StatItem label="Invoice Wait" value={size?.invoice_wait} />
            <StatItem label="Another Invoice" value={size?.another_invoice} />
            <StatItem label="Total" value={size?.total_invoice} />
          </Card>

          {/* Score Request */}
          <Card icon={FiAward} title="Score Request" color="bg-blue-600">
            <StatItem label="Score Validation" value={size?.score_validation} />
            <StatItem label="Score Request" value={size?.score_request} />
            <StatItem label="Score Pending" value={size?.score_pending} />
            <StatItem label="Total" value={size?.total_score_request} />
          </Card>

          {/* MOHE */}
          <Card icon={FiTruck} title="MOHE" color="bg-purple-600">
            <StatItem label="MOHE Validation" value={size?.mohe_validation} />
            <StatItem label="Sending to MOHE" value={size?.mohe} />
          </Card>

          {/* Done */}
          <Card icon={FiCheckCircle} title="Done" color="bg-teal-600">
            <StatItem label="Done" value={size?.done} />
          </Card>
        </div>
      )}
    </div>
  );
}
